import moment from "moment";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import NarIngestionReUpload from "../../../../components/modals/narIngestionReUpload/narIngestionReUpload";
import NarIngestionUpload from "../../../../components/modals/narIngestionUpload/narIngestionUpload";
import NewTabElements from "../../../../components/newTabElements/newTabElements";
import { ModalId } from "../../../../enums/modalId";
import { RootState } from "../../../../redux/store/store";
import { capitalizeWordsSeperatedByUnderScore } from "../../../../utils/stringModifications";
import CsvAndPdfTab from "./components/csvAndPdfTab";
import GridCard from "./components/gridCard";
import Header from "./components/header";
import { useFetchGridInfo } from "./hooks/fetchGridInfo";
import { useNarIngestionModal } from "./hooks/useNarIngestionModal";
import { getLastDateOfMonth } from "./utils/getLastDay";

const NarConfigurationLayout = () => {
  const [activeTab, setActiveTab] = useState("logs");
  const [ingestionSearch, setIngestionSearch] = useState("");
  const {
    data,
    loading,
    searchAllBranchesFailed,
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
  } = useFetchGridInfo(10, ingestionSearch);
  const { handleCloseModal, metaData } = useNarIngestionModal();
  const { openedModalId } = useSelector((state: RootState) => state.modal);

  const tabs = [
    { id: "logs", label: "Ingestion Logs" },
    { id: "csv-and-pdf", label: "Configuration" },
  ];

  return (
    <>
      <div className="nar-configuration-layout flex w-full flex-col gap-4 overflow-y-auto px-2 pb-2">
        <Header />
        <div className="tabs-container flex flex-shrink-0 gap-2 border-b border-primaryGray-16">
          <NewTabElements
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(tab) => setActiveTab(tab)}
          />
        </div>
        {activeTab === "logs" && (
          <>
            <div className="flex w-full flex-shrink-0 items-center gap-3 py-1">
              <div className="min-w-0 max-w-md flex-1">
                <input
                  type="search"
                  aria-label="Search NAR ingestion logs by insurance or plan"
                  placeholder="Search logs by insurance or plan"
                  value={ingestionSearch}
                  onChange={(e) => setIngestionSearch(e.target.value)}
                  className="focus:border-primaryBlue-7 w-full rounded-md border border-primaryGray-15 px-3 py-2 text-small text-primaryGray-1 outline-none"
                />
              </div>
              <div className="flex flex-shrink-0 items-center gap-3">
                <Button
                  buttonType="secondary"
                  size="small"
                  disabled={loading || !hasPreviousPage}
                  onClick={goToPreviousPage}
                >
                  Previous
                </Button>
                <Button
                  buttonType="secondary"
                  size="small"
                  disabled={loading || !hasNextPage}
                  onClick={goToNextPage}
                >
                  Next
                </Button>
              </div>
            </div>
            {loading ? (
              <div className="flex h-full w-full flex-row items-center justify-center gap-2 py-8">
                <LoaderMessage message="Loading NAR Ingestion Logs..." />
              </div>
            ) : searchAllBranchesFailed ? (
              <div className="py-6 text-center text-small text-primaryGray-8">
                Search failed. Try again or adjust your search.
              </div>
            ) : data.length === 0 ? (
              <div className="py-6 text-center text-small text-primaryGray-8">
                {ingestionSearch.trim()
                  ? "No ingestion logs match this search."
                  : "No ingestion logs to display."}
              </div>
            ) : (
              data.map((item) => {
                const createdOnFormatted = moment(item.created_at_time).format(
                  "MM/DD/YYYY HH:mm",
                );

                return (
                  <GridCard
                    key={item.id}
                    insurance={capitalizeWordsSeperatedByUnderScore(
                      item.payer_type,
                    )}
                    insurancePlan={capitalizeWordsSeperatedByUnderScore(
                      item.sub_type,
                    )}
                    createdOn={createdOnFormatted}
                    expiryDates={getLastDateOfMonth(item.month, item.year)}
                    status={item.status}
                    processedCsvPath={item.processed_csv_path}
                    outputFileName={item.output_file_name}
                    payerType={item.payer_type}
                    subType={item.sub_type}
                    month={item.month}
                    year={item.year}
                    documentType={item.document_type}
                    documentId={item.id}
                    isRejected={item.is_rejected}
                    sourceFileName={item?.source_file}
                    effectiveDate={item?.effective_date}
                    neverExpire={Boolean(item?.never_expire)}
                  />
                );
              })
            )}
          </>
        )}
        {activeTab === "csv-and-pdf" && <CsvAndPdfTab />}
      </div>
      {openedModalId === ModalId.NAR_INGESTION_UPLOAD_MODAL && (
        <NarIngestionUpload onClose={handleCloseModal} />
      )}
      {openedModalId === ModalId.NAR_INGESTION_RE_UPLOAD_MODAL && (
        <NarIngestionReUpload
          onClose={handleCloseModal}
          csv_path={metaData?.csv_path || ""}
        />
      )}
    </>
  );
};

export default NarConfigurationLayout;
